/**
 * /Recon portal — single engagement detail.
 *
 * Shows the client what's in scope, current status, scope of work,
 * and which deliverables are queued / generating / published.
 *
 * Phase 2b ships read-only. Phase 2c adds:
 *   - Document download for published reports + bundles
 *   - Document upload (client → assessor)
 *   - Messages thread
 *   - Re-test scheduling
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  getApiBaseUrl,
  getEngagementActivityApiV1ClientPortalEngagementsCaseIdActivityGet,
  getEngagementApiV1ClientPortalEngagementsCaseIdGet,
} from "@hackereyes/api-client";
import { useClientAuth } from "@/auth/client-auth-context";
import { ShareCreateModal } from "./share-create-modal";
import "./engagement-detail.css";

interface Target {
  id: string;
  display_name: string;
  primary_identifier: string;
  target_type: string;
  priority: string;
}

interface Suggestion extends Target {
  created_at: string;
}

interface Report {
  id: string;
  title: string;
  report_type: string;
  status: string;
  generated_at: string | null;
  published_at: string | null;
  created_at: string;
}

interface Engagement {
  id: string;
  case_number: string;
  title: string;
  status: string;
  case_type: string;
  practice_area: string;
  jurisdiction: string;
  scope_of_work: string;
  engagement_letter_signed_at: string | null;
  payment_status: string | null;
  created_at: string;
  opened_at: string | null;
  closed_at: string | null;
  targets: Target[];
  suggestions?: Suggestion[];
  reports: Report[];
}

const STATUS_COPY: Record<string, { label: string; tone: string; lede: string }> = {
  intake: {
    label: "Awaiting authorization",
    tone: "amber",
    lede: "We’re preparing your authorization. Once signed, the engagement begins.",
  },
  pending: {
    label: "Awaiting payment",
    tone: "amber",
    lede: "Authorization is signed. We’ll begin once payment is processed.",
  },
  active: {
    label: "In progress",
    tone: "cyan",
    lede: "The assessment is underway. Deliverables appear here when ready for your review.",
  },
  review: {
    label: "Pending final review",
    tone: "cobalt",
    lede: "Your assessor is finalizing deliverables. We’ll notify you when they’re ready.",
  },
  delivered: {
    label: "Report ready",
    tone: "green",
    lede: "Your report is ready. Open it to review findings, severity, and remediation steps. Once you've read through it, you can act on individual findings from the action items view.",
  },
  closed: {
    label: "Closed",
    tone: "slate",
    lede: "This engagement is closed. Your report remains available below.",
  },
};

const REPORT_STATUS_COPY: Record<string, { label: string; tone: string }> = {
  draft: { label: "Draft", tone: "slate" },
  generating: { label: "Generating", tone: "amber" },
  review: { label: "In review", tone: "cobalt" },
  approved: { label: "Approved", tone: "cyan" },
  published: { label: "Published", tone: "green" },
};

export function EngagementDetail() {
  const { caseId } = useParams<{ caseId: string }>();
  const { accessToken } = useClientAuth();
  // When set, opens the share-create modal targeting the chosen report.
  // Stored as a Report so we can pass title + id without re-finding it
  // in the data tree on the modal mount.
  const [shareModalReport, setShareModalReport] = useState<Report | null>(null);
  // Per-report download state so the button can show "Downloading…"
  // and surface errors. Keyed by report id.
  const [pdfDownload, setPdfDownload] = useState<
    Record<string, "idle" | "loading" | "error">
  >({});

  /** Fetches the PDF blob with the bearer token and triggers a
   *  browser download. Uses raw fetch instead of the SDK so we
   *  control the response handling (Blob) and don't fight with the
   *  generated client's default JSON parsing. */
  async function downloadReportPdf(
    caseIdArg: string,
    reportId: string,
    reportTitle: string,
  ): Promise<void> {
    setPdfDownload((s) => ({ ...s, [reportId]: "loading" }));
    try {
      const url = `${getApiBaseUrl()}/api/v1/client-portal/engagements/${encodeURIComponent(caseIdArg)}/reports/${encodeURIComponent(reportId)}/pdf`;
      const headers: Record<string, string> = {};
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      const response = await fetch(url, {
        headers,
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const safeTitle =
        reportTitle.replace(/[^\w-]+/g, "_").slice(0, 80) || "report";
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${safeTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      setPdfDownload((s) => ({ ...s, [reportId]: "idle" }));
    } catch {
      setPdfDownload((s) => ({ ...s, [reportId]: "error" }));
    }
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ["client-engagement", caseId],
    queryFn: async () => {
      const { data, error } = await getEngagementApiV1ClientPortalEngagementsCaseIdGet({
        path: { case_id: caseId! },
      });
      if (error) {
        const detail = (error as Record<string, unknown>)?.detail as string;
        throw new Error(detail || "load_failed");
      }
      return data as unknown as Engagement;
    },
    enabled: !!caseId,
  });

  if (isLoading) {
    return (
      <div className="engagement-detail">
        <div className="engagement-detail-inner">
          <p className="engagement-detail-loading">Loading engagement…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="engagement-detail">
        <div className="engagement-detail-inner">
          <Link to="/portal" className="engagement-detail-back">← All engagements</Link>
          <div className="engagement-detail-error">
            We couldn’t load this engagement. It may not exist, or you may not
            have access to it.
          </div>
        </div>
      </div>
    );
  }

  const status = STATUS_COPY[data.status] || {
    label: data.status,
    tone: "slate",
    lede: "Status update will appear here when available.",
  };

  const created = new Date(data.created_at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  // Primary published report — the one the buyer probably wants to
  // open first. Picks the most recently published; the deliverables
  // section below shows the full list. Used to surface a hero CTA
  // when the engagement is delivered, so the report is the first
  // action available, not buried under scope + assets + activity.
  const primaryPublishedReport = (() => {
    const published = data.reports.filter((r) => r.status === "published");
    if (published.length === 0) return null;
    return published.reduce((latest, r) => {
      const latestT = latest.published_at || latest.generated_at || latest.created_at;
      const rT = r.published_at || r.generated_at || r.created_at;
      return new Date(rT) > new Date(latestT) ? r : latest;
    });
  })();

  return (
    <div className="engagement-detail">
      <div className="engagement-detail-inner">
        <Link to="/portal" className="engagement-detail-back">← All engagements</Link>

        {/* ---- Hero ---- */}
        <header className="engagement-hero">
          <div className="engagement-hero-meta">
            <span className="engagement-ref">{data.case_number}</span>
            <span className={`engagement-status engagement-status-${status.tone}`}>
              {status.label}
            </span>
          </div>
          <h1 className="engagement-hero-title">{data.title}</h1>
          <p className="engagement-hero-lede">{status.lede}</p>
          <div className="engagement-hero-grid">
            <Fact label="Engagement type" value={data.case_type.replace(/_/g, " ")} />
            <Fact label="Started" value={created} />
            <Fact
              label="Authorization"
              value={data.engagement_letter_signed_at ? "Signed" : "Pending"}
            />
            <Fact
              label="Payment"
              value={(data.payment_status || "n/a").replace(/_/g, " ")}
            />
          </div>
          {(!data.payment_status ||
            data.payment_status === "pending" ||
            data.payment_status === "not_charged") && (
            <div className="engagement-hero-cta">
              <Link
                to={`/portal/commission/success?case=${encodeURIComponent(data.case_number)}`}
                className="recon-btn recon-btn-primary recon-btn-lg"
              >
                Continue to payment →
              </Link>
              <span className="engagement-hero-cta-hint">
                Your engagement was authorized but payment wasn't completed.
                Pick up where you left off.
              </span>
            </div>
          )}

          {/* Report-ready hero CTA. When the engagement has a published
           *  report, the most important action is "open it" — not
           *  "review scope" or "act on findings." Surface a big primary
           *  link so the buyer doesn't have to scroll past the
           *  Deliverables section heading and three smaller buttons. */}
          {primaryPublishedReport && (
            <div className="engagement-hero-cta">
              <Link
                to={`/portal/engagements/${data.id}/reports/${primaryPublishedReport.id}/html`}
                className="recon-btn recon-btn-primary recon-btn-lg"
              >
                Open your report →
              </Link>
              <span className="engagement-hero-cta-hint">
                {primaryPublishedReport.title}
              </span>
            </div>
          )}
        </header>

        {/* ---- Scope ---- */}
        <section className="engagement-section">
          <h2 className="engagement-section-title">Scope of work</h2>
          <p className="engagement-scope-text">
            {data.scope_of_work || "No scope statement on file."}
          </p>
        </section>

        {/* ---- Targets ---- */}
        {data.targets.length > 0 && (
          <section className="engagement-section">
            <h2 className="engagement-section-title">Assets in scope</h2>
            <ul className="engagement-target-list">
              {data.targets.map((t) => (
                <li key={t.id} className="engagement-target">
                  <span className="engagement-target-id">{t.primary_identifier}</span>
                  <span className="engagement-target-name">{t.display_name}</span>
                  <span className="engagement-target-type">{t.target_type.replace(/_/g, " ")}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Suggestions section retired. Scope discovery now happens
            at commission time inside the configurator; the buyer
            already cherry-picked their in-scope subdomains there.
            Showing an additional "we found these, pending your
            decision" list on the engagement detail confused the
            'action items' surface and contradicted the upfront-
            scoping flow. If post-engagement scope expansion ever
            comes back as a product (e.g. "add this to a follow-up
            engagement"), that's a separate component with its own
            CTA. */}

        {/* ---- Deliverables ---- */}
        <section className="engagement-section">
          <h2 className="engagement-section-title">Deliverables</h2>
          {data.reports.length === 0 ? (
            <div className="engagement-deliverables-empty">
              Deliverables haven’t been generated yet. They appear here as your
              assessor finalizes the report and supporting documents.
            </div>
          ) : (
            <div className="engagement-report-list">
              {data.reports.map((r) => {
                const rstatus = REPORT_STATUS_COPY[r.status] || {
                  label: r.status, tone: "slate",
                };
                const isPublished = r.status === "published";
                return (
                  <div key={r.id} className="engagement-report-row">
                    <div className="engagement-report-left">
                      <div className="engagement-report-title">{r.title}</div>
                      <div className="engagement-report-meta">
                        {r.report_type.replace(/_/g, " ")}
                      </div>
                    </div>
                    <div className="engagement-report-right">
                      <span className={`engagement-status engagement-status-${rstatus.tone}`}>
                        {rstatus.label}
                      </span>
                      {isPublished ? (
                        <>
                          <Link
                            to={`/portal/engagements/${data.id}/reports/${r.id}/html`}
                            className="engagement-report-cta engagement-report-cta-primary"
                          >
                            Open report →
                          </Link>
                          <button
                            type="button"
                            className="engagement-report-cta engagement-report-cta-secondary"
                            onClick={() => downloadReportPdf(data.id, r.id, r.title)}
                            disabled={pdfDownload[r.id] === "loading"}
                          >
                            {pdfDownload[r.id] === "loading"
                              ? "Downloading…"
                              : pdfDownload[r.id] === "error"
                                ? "Retry PDF"
                                : "Download PDF"}
                          </button>
                          <Link
                            to={`/portal/engagements/${data.id}/reports/${r.id}/actions`}
                            className="engagement-report-cta engagement-report-cta-secondary"
                          >
                            Take action on findings
                          </Link>
                          <button
                            type="button"
                            className="engagement-report-cta engagement-report-cta-tertiary"
                            onClick={() => setShareModalReport(r)}
                          >
                            Share
                          </button>
                        </>
                      ) : (
                        <span className="engagement-report-cta engagement-report-cta-disabled">
                          Not yet available
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ---- Activity feed (Bucket QQ-W3) ---- */}
        <section className="engagement-section">
          <h2 className="engagement-section-title">Activity</h2>
          {caseId && <ActivityFeed caseId={caseId} />}
        </section>

        {/* Follow-up call callout retired. The copy was tease text
            ('you'll see a scheduling link here') for a feature that
            isn't wired up. When the scheduling integration actually
            ships, re-add this block with a live booking link. */}
      </div>

      {shareModalReport && caseId && (
        <ShareCreateModal
          caseId={caseId}
          reportId={shareModalReport.id}
          reportTitle={shareModalReport.title}
          onClose={() => setShareModalReport(null)}
        />
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="engagement-fact">
      <div className="engagement-fact-label">{label}</div>
      <div className="engagement-fact-value">{value}</div>
    </div>
  );
}

/* --- Activity feed (Bucket QQ-W3) ----------------------------------- */

interface ActivityEntry {
  kind: "recipient_access" | "comment" | "action";
  happened_at: string;
  actor: string;
  summary: string;
  detail: Record<string, unknown>;
}

const KIND_LABEL: Record<ActivityEntry["kind"], string> = {
  recipient_access: "Share",
  comment: "Comment",
  action: "Status",
};

function ActivityFeed({ caseId }: { caseId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["engagement-activity", caseId],
    queryFn: async () => {
      const { data, error } =
        await getEngagementActivityApiV1ClientPortalEngagementsCaseIdActivityGet({
          path: { case_id: caseId },
        });
      if (error) throw new Error("load_failed");
      return (data ?? []) as unknown as ActivityEntry[];
    },
  });

  if (isLoading) {
    return <p className="activity-feed-loading">Loading activity…</p>;
  }
  if (error) {
    return (
      <p className="activity-feed-error">
        Couldn't load the activity feed. Refresh, or contact the assessor.
      </p>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="activity-feed-empty">
        No activity yet. As your report publishes, recipients view it, and
        you work through findings, the events appear here newest first.
      </p>
    );
  }

  return (
    <ul className="activity-feed">
      {data.map((entry, i) => (
        <li key={`${entry.kind}-${i}`} className={`activity-row activity-row-${entry.kind}`}>
          <div className="activity-row-meta">
            <span className={`activity-kind activity-kind-${entry.kind}`}>
              {KIND_LABEL[entry.kind]}
            </span>
            <span className="activity-time">
              {new Date(entry.happened_at).toLocaleString(undefined, {
                month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
              })}
            </span>
          </div>
          <div className="activity-row-body">
            <span className="activity-actor">{entry.actor}</span>
            <span className="activity-summary">{entry.summary}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
